import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { firmAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Initialize Stripe outside component
let stripePromise: Promise<Stripe | null> | null = null;

const getStripe = (publishableKey: string) => {
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

interface PaymentMethod {
  id: number;
  stripe_payment_method_id: string;
  type: string;
  card_brand: string;
  card_last4: string;
  card_exp_month: number;
  card_exp_year: number;
  is_default: boolean;
  created_at: string;
}

interface AutoPaySettings {
  id: number;
  client_firm_id: number;
  stripe_payment_method_id: number | null;
  is_enabled: boolean;
  threshold_amount: number;
  auto_pay_amount: number;
  last_auto_pay_at: string | null;
  payment_method?: PaymentMethod;
}

const AddCardForm: React.FC<{
  clientSecret: string;
  onSuccess: () => void;
  onError: (message: string) => void;
  onClose: () => void;
}> = ({ clientSecret, onSuccess, onError, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      setProcessing(false);
      return;
    }

    try {
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        }
      });

      if (confirmError) {
        setError(confirmError.message || 'Failed to save card');
        onError(confirmError.message || 'Failed to save card');
      } else if (setupIntent && setupIntent.payment_method) {
        onSuccess();
      } else {
        setError('Failed to save card');
        onError('Failed to save card');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'An error occurred';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <CardElement options={cardElementOptions} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DialogActions>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || processing}
        >
          {processing ? <CircularProgress size={24} /> : 'Add Card'}
        </Button>
      </DialogActions>
    </form>
  );
};

const AutoPayManagement: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [autoPaySettings, setAutoPaySettings] = useState<AutoPaySettings | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addCardDialog, setAddCardDialog] = useState(false);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [thresholdAmount, setThresholdAmount] = useState('');
  const [autoPayAmount, setAutoPayAmount] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  const hasAccess = () => {
    return isPmpAdminOrSiteAdmin() || user?.firm_id === parseInt(firmId!) || user?.firm?.id === parseInt(firmId!);
  };

  useEffect(() => {
    if (!hasAccess()) {
      setError('You do not have access to this page');
      setLoading(false);
      return;
    }
    fetchData();
    fetchStripeKey();
  }, [firmId]);

  const fetchStripeKey = async () => {
    try {
      const response = await firmAPI.getStripePublishableKey();
      if (response.data.success) {
        const key = response.data.data?.publishable_key || response.data.publishable_key;
        if (key) {
          setStripePublishableKey(key);
        }
      }
    } catch (err: any) {
      // Stripe might not be configured
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Ensure Stripe customer exists
      await firmAPI.getOrCreateStripeCustomer(parseInt(firmId!));

      const [paymentMethodsRes, settingsRes] = await Promise.all([
        firmAPI.getPaymentMethods(parseInt(firmId!)),
        firmAPI.getAutoPaySettings(parseInt(firmId!)),
      ]);

      if (paymentMethodsRes.data.success) {
        setPaymentMethods(paymentMethodsRes.data.data || []);
      }

      if (settingsRes.data.success) {
        const settings = settingsRes.data.data;
        if (settings) {
          setAutoPaySettings(settings);
          setThresholdAmount(settings.threshold_amount.toString());
          setAutoPayAmount(settings.auto_pay_amount.toString());
          setIsEnabled(settings.is_enabled);
          setSelectedPaymentMethodId(settings.stripe_payment_method_id);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async () => {
    if (!stripePublishableKey) {
      setError('Stripe is not configured');
      return;
    }

    try {
      setError('');
      const response = await firmAPI.createSetupIntent(parseInt(firmId!));
      
      if (response.data.success) {
        setSetupIntentClientSecret(response.data.data.client_secret);
        setAddCardDialog(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create setup intent');
    }
  };

  const handleCardAdded = async () => {
    try {
      setSubmitting(true);
      setError('');
      
      // Get the setup intent to retrieve payment method ID
      if (!stripePublishableKey || !setupIntentClientSecret) {
        setError('Missing setup intent information');
        return;
      }

      const stripe = await getStripe(stripePublishableKey);
      if (!stripe) {
        setError('Stripe not initialized');
        return;
      }

      const setupIntent = await stripe.retrieveSetupIntent(setupIntentClientSecret);
      const paymentMethodId = setupIntent.setupIntent?.payment_method;

      if (!paymentMethodId || typeof paymentMethodId !== 'string') {
        setError('Failed to retrieve payment method');
        return;
      }

      const response = await firmAPI.attachPaymentMethod(parseInt(firmId!), {
        payment_method_id: paymentMethodId,
      });

      if (response.data.success) {
        setSuccess('Card added successfully');
        setAddCardDialog(false);
        setSetupIntentClientSecret(null);
        await fetchData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to add card');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetDefault = async (paymentMethodId: number) => {
    try {
      setError('');
      const response = await firmAPI.setDefaultPaymentMethod(parseInt(firmId!), paymentMethodId);

      if (response.data.success) {
        setSuccess('Default payment method updated');
        await fetchData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to set default payment method');
    }
  };

  const handleRemoveCard = async (paymentMethodId: number) => {
    if (!window.confirm('Are you sure you want to remove this card?')) {
      return;
    }

    try {
      setError('');
      const response = await firmAPI.removePaymentMethod(parseInt(firmId!), paymentMethodId);

      if (response.data.success) {
        setSuccess('Card removed successfully');
        await fetchData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove card');
    }
  };

  const handleSaveSettings = async () => {
    if (!thresholdAmount || parseFloat(thresholdAmount) <= 0) {
      setError('Please enter a valid threshold amount');
      return;
    }

    if (!autoPayAmount || parseFloat(autoPayAmount) <= 0) {
      setError('Please enter a valid auto-pay amount');
      return;
    }

    if (isEnabled && !selectedPaymentMethodId) {
      setError('Please select a payment method for auto-pay');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const response = await firmAPI.updateAutoPaySettings(parseInt(firmId!), {
        is_enabled: isEnabled,
        threshold_amount: parseFloat(thresholdAmount),
        auto_pay_amount: parseFloat(autoPayAmount),
        stripe_payment_method_id: isEnabled ? selectedPaymentMethodId : null,
      });

      if (response.data.success) {
        setSuccess('Auto-pay settings saved successfully');
        setSettingsDialog(false);
        await fetchData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCardNumber = (last4: string) => {
    return `•••• •••• •••• ${last4}`;
  };

  const formatExpiry = (month: number, year: number) => {
    return `${month.toString().padStart(2, '0')}/${year}`;
  };

  const getCardBrandIcon = (brand: string) => {
    return brand?.toLowerCase() || 'card';
  };

  if (!hasAccess()) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">You do not have access to this page</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="#" onClick={() => navigate('/firms')}>
          Firms
        </Link>
        <Link color="inherit" href="#" onClick={() => navigate(`/firms/${firmId}`)}>
          Firm Details
        </Link>
        <Typography color="text.primary">Auto-Pay Management</Typography>
      </Breadcrumbs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Auto-Pay Settings Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Auto-Pay Settings</Typography>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => {
              setSettingsDialog(true);
            }}
          >
            Configure
          </Button>
        </Box>

        {autoPaySettings ? (
          <Box>
            <FormControlLabel
              control={<Switch checked={autoPaySettings.is_enabled} disabled />}
              label={autoPaySettings.is_enabled ? 'Auto-Pay Enabled' : 'Auto-Pay Disabled'}
            />
            {autoPaySettings.is_enabled && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1">
                  <strong>Threshold:</strong> ${parseFloat(autoPaySettings.threshold_amount.toString()).toFixed(2)}
                </Typography>
                <Typography variant="body1">
                  <strong>Auto-Pay Amount:</strong> ${parseFloat(autoPaySettings.auto_pay_amount.toString()).toFixed(2)}
                </Typography>
                {autoPaySettings.payment_method && (
                  <Typography variant="body1">
                    <strong>Payment Method:</strong> {formatCardNumber(autoPaySettings.payment_method.card_last4)}
                  </Typography>
                )}
                {autoPaySettings.last_auto_pay_at && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Last auto-pay: {new Date(autoPaySettings.last_auto_pay_at).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Auto-pay is not configured. Click Configure to set it up.
          </Typography>
        )}
      </Paper>

      {/* Payment Methods */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Payment Methods</Typography>
          {stripePublishableKey && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddCard}
            >
              Add Card
            </Button>
          )}
        </Box>

        {paymentMethods.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No payment methods added yet. Click "Add Card" to add one.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {paymentMethods.map((method) => (
              <Box key={method.id} sx={{ minWidth: 300, maxWidth: 400, flex: '1 1 300px' }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <CreditCardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                      {method.is_default && (
                        <Chip
                          icon={<StarIcon />}
                          label="Default"
                          color="primary"
                          size="small"
                        />
                      )}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {formatCardNumber(method.card_last4)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {method.card_brand?.toUpperCase()} • Expires {formatExpiry(method.card_exp_month, method.card_exp_year)}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    {!method.is_default && (
                      <Button
                        size="small"
                        startIcon={<StarBorderIcon />}
                        onClick={() => handleSetDefault(method.id)}
                      >
                        Set Default
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveCard(method.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Add Card Dialog */}
      {stripePublishableKey && setupIntentClientSecret && (
        <Dialog open={addCardDialog} onClose={() => setAddCardDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Payment Method</DialogTitle>
          <DialogContent>
            <Elements stripe={getStripe(stripePublishableKey)} options={{ clientSecret: setupIntentClientSecret }}>
              <AddCardForm
                clientSecret={setupIntentClientSecret}
                onSuccess={handleCardAdded}
                onError={(message) => setError(message)}
                onClose={() => {
                  setAddCardDialog(false);
                  setSetupIntentClientSecret(null);
                }}
              />
            </Elements>
          </DialogContent>
        </Dialog>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onClose={() => setSettingsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure Auto-Pay</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                />
              }
              label="Enable Auto-Pay"
            />

            <TextField
              fullWidth
              label="Threshold Amount ($)"
              type="number"
              value={thresholdAmount}
              onChange={(e) => setThresholdAmount(e.target.value)}
              sx={{ mt: 2 }}
              inputProps={{ min: 0.01, step: 0.01 }}
              helperText="When balance goes below this amount, auto-pay will trigger"
              required
            />

            <TextField
              fullWidth
              label="Auto-Pay Amount ($)"
              type="number"
              value={autoPayAmount}
              onChange={(e) => setAutoPayAmount(e.target.value)}
              sx={{ mt: 2 }}
              inputProps={{ min: 0.01, step: 0.01 }}
              helperText="Amount to charge when threshold is reached"
              required
            />

            {isEnabled && paymentMethods.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Select Payment Method
                </Typography>
                {paymentMethods.map((method) => (
                  <Card
                    key={method.id}
                    sx={{
                      mt: 1,
                      cursor: 'pointer',
                      border: selectedPaymentMethodId === method.id ? 2 : 1,
                      borderColor: selectedPaymentMethodId === method.id ? 'primary.main' : 'divider',
                    }}
                    onClick={() => setSelectedPaymentMethodId(method.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body1">
                            {formatCardNumber(method.card_last4)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {method.card_brand?.toUpperCase()} • Expires {formatExpiry(method.card_exp_month, method.card_exp_year)}
                          </Typography>
                        </Box>
                        {method.is_default && (
                          <Chip label="Default" color="primary" size="small" />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {isEnabled && paymentMethods.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please add a payment method before enabling auto-pay.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            disabled={submitting || (isEnabled && paymentMethods.length === 0)}
          >
            {submitting ? <CircularProgress size={24} /> : 'Save Settings'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AutoPayManagement;

